'use client';
import React, { useEffect } from 'react';
import Link from 'next/link';
import { ModuleWrapper } from './ModuleWrapper';
import { ModuleTable } from './ModuleTable';
import { SessionContext } from '@benzinga/session-context';
import { CalendarManager, CompanyEventDetails } from '@benzinga/calendar-manager';
import { DateTime } from 'luxon';
import { formatDatePretty } from '@benzinga/date-utils';
import { SimpleTextCell, TextCell } from './TableCells';
import type { ColumnsType } from 'antd/es/table';

const CALENDAR_MAX_DAYS = 30;

const dedupeSecurities = (securities: CompanyEventDetails['securities']) =>
  Array.from(new Map(securities.map(security => [security.symbol, security])).values());

const columns: ColumnsType<CompanyEventDetails> = [
  {
    key: 'event_name',
    title: 'Event Name',
    dataIndex: 'event_name',
    width: 300,
    render: (_value, record) => <TextCell className="font-semibold">{record.event_name}</TextCell>,
  },
  {
    key: 'securities',
    title: 'Tickers',
    dataIndex: 'securities',
    width: 300,
    render: (_value, record) => (
      <div className="flex flex-wrap gap-1">
        {record.securities.map((security, index) => (
          <span key={security.symbol}>
            <Link
              href={`/quote/${security.symbol}`}
              className="font-semibold text-black no-underline hover:text-blue-500"
              prefetch={false}
            >
              {security.symbol}
            </Link>
            {index < record.securities.length - 1 && <span className="text-gray-500">,</span>}
          </span>
        ))}
      </div>
    ),
  },
  {
    key: 'date_start',
    title: 'Date',
    dataIndex: 'date_start',
    render: (_value, record) => (
      <SimpleTextCell color="text-gray-600">
        {record.date_start
          ? formatDatePretty(record.date_start, { timePeriod: { month: 'short', day: 'numeric', year: 'numeric' } })
          : '--'}
      </SimpleTextCell>
    ),
  },
];

const CompanyEventsModule: React.FC = () => {
  const session = React.useContext(SessionContext);
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<CompanyEventDetails[]>([]);

  useEffect(() => {
    const calendarManager = session.getManager(CalendarManager);
    calendarManager
      .getCompanyEvents({})
      .then(response => {
        const events: Record<string, CompanyEventDetails> = {};

        if (Array.isArray(response?.ok)) {
          response?.ok
            .sort((a, b) => DateTime.fromISO(a.date_start).toMillis() - DateTime.fromISO(b.date_start).toMillis())
            .forEach(companyEvent => {
              if (
                DateTime.fromISO(companyEvent.date_start) >= DateTime.now().startOf('day') &&
                DateTime.fromISO(companyEvent.date_start) <=
                  DateTime.now().plus({ days: CALENDAR_MAX_DAYS }).endOf('day')
              ) {
                if (!events[companyEvent.event_name]) {
                  events[companyEvent.event_name] = {
                    ...companyEvent,
                    securities: dedupeSecurities(companyEvent.securities),
                  };
                } else {
                  events[companyEvent.event_name].securities = dedupeSecurities([
                    ...events[companyEvent.event_name].securities,
                    ...companyEvent.securities,
                  ]);
                }
              }
            });
        }

        setData(Object.values(events));
      })
      .catch(error => {
        console.error('Error fetching company events:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [session]);

  return (
    <>
    <ModuleWrapper title="Company Events">
      <ModuleTable
        columns={columns}
        data={data.map((item, index) => ({ ...item, key: `${item.event_name}-${index}` }))}
        loading={loading}
        emptyText="No upcoming company events"
      />
    </ModuleWrapper>
  </>
  );
};  

export default CompanyEventsModule;
